/**
 * pages/AccueilArrivantV2.js
 * The Crowd v2 — Gestion utilisateurs › Accueil nouvel arrivant
 *
 * Adaptation v2 de pages/CreationCompte.js : meme appel API
 * (envoyerCreationCompte), meme charge utile, memes regles de generation
 * du login et de l'adresse, meme recherche des managers dans Entra.
 *
 * Disposition plein ecran : le nouvel arrivant a gauche (identite,
 * identifiants), l'envoi a droite (destinataires, recapitulatif,
 * bouton).
 *
 * Correction de securite : le mot de passe temporaire est tire avec
 * crypto.getRandomValues au lieu de Math.random (non cryptographique),
 * et melange par Fisher-Yates au lieu d'un tri aleatoire (biaise).
 * Meme format qu'avant : 14 caracteres, 2 majuscules, 2 minuscules,
 * 2 chiffres, 2 speciaux au minimum.
 */

import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { envoyerCreationCompte } from "../services/api";
import { rechercherUtilisateursEntra } from "../services/annuaire";
import SelecteurPersonnes from "../components/SelecteurPersonnes";
import IconeV2 from "../components/IconeV2";

// ---------------------------------------------------------------------------
// Mot de passe temporaire (aleatoire cryptographique)
// ---------------------------------------------------------------------------

function aleatoire(n) {
  // Entier uniforme dans [0, n[, sans biais de modulo
  const tableau = new Uint32Array(1);
  const plafond = Math.floor(0x100000000 / n) * n;
  let x;
  do {
    window.crypto.getRandomValues(tableau);
    x = tableau[0];
  } while (x >= plafond);
  return x % n;
}

function genererMotDePasse() {
  const maj = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const min = "abcdefghjkmnpqrstuvwxyz";
  const chif = "23456789";
  const spec = "@#$%&*!?";
  const tous = maj + min + chif + spec;
  const tirer = (jeu) => jeu[aleatoire(jeu.length)];
  const mdp = [tirer(maj), tirer(maj), tirer(min), tirer(min), tirer(chif), tirer(chif), tirer(spec), tirer(spec)];
  for (let i = 0; i < 6; i++) mdp.push(tirer(tous));
  // Melange de Fisher-Yates
  for (let i = mdp.length - 1; i > 0; i--) {
    const j = aleatoire(i + 1);
    [mdp[i], mdp[j]] = [mdp[j], mdp[i]];
  }
  return mdp.join("");
}

// ---------------------------------------------------------------------------
// Login et adresse proposes
// ---------------------------------------------------------------------------

function slugify(str) {
  return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
}

function genererLogin(prenom, nom) {
  if (!prenom || !nom) return "";
  return `${slugify(prenom)[0] || ""}${slugify(nom)}@hopscotchgroupe.com`;
}

function genererEmail(prenom, nom) {
  if (!prenom || !nom) return "";
  return `${slugify(prenom)[0] || ""}${slugify(nom)}@hopscotch.one`;
}

const formulaireVide = () => ({
  prenom: "", nom: "", login: "", email: "", telephone: "", mot_de_passe: genererMotDePasse(),
});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AccueilArrivantV2({ collaborateur }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [form, setForm] = useState(formulaireVide);
  const [managers, setManagers] = useState([]);
  const [wedo, setWedo] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [mdpVisible, setMdpVisible] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError(null);
    setSuccess(null);
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "prenom" || name === "nom") {
        const p = name === "prenom" ? value : f.prenom;
        const n = name === "nom" ? value : f.nom;
        // Ne regenere que si l'operateur n'a pas modifie le champ a la main
        if (!f._loginEdited) next.login = genererLogin(p, n);
        if (!f._emailEdited) next.email = genererEmail(p, n);
      }
      if (name === "login") next._loginEdited = true;
      if (name === "email") next._emailEdited = true;
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.prenom.trim() || !form.nom.trim() || !form.login.trim() || !form.mot_de_passe.trim()) {
      setError("Prénom, nom, login et mot de passe sont obligatoires.");
      return;
    }
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await envoyerCreationCompte({
        collaborateur_id: collaborateur.id,
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        login: form.login.trim(),
        email: form.email.trim() || form.login.trim(),
        telephone: form.telephone.trim(),
        mot_de_passe: form.mot_de_passe.trim(),
        managers: managers.map((m) => ({ email: m.email, nom: m.displayName })),
        wedo,
      });
      setSuccess(`Mail envoyé à : ${data.destinataires.join(", ")}`);
      setForm(formulaireVide());
      setManagers([]);
    } catch (e) {
      setError(e.message || "Erreur lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  const destinataires = [
    form.email || form.login,
    ...managers.map((m) => m.email),
    collaborateur?.prenom && `${collaborateur.prenom} ${collaborateur.nom} (vous)`,
    "otaleb@hopscotchgroupe.com",
    "jirajdc@hopscotch.one",
    wedo && "alemaitre@hopscotch.one",
  ].filter(Boolean);

  return (
    <div className="v2-page v2-page-module">
      <header className="v2-module-entete">
        <h1 className="v2-module-titre">Accueil nouvel arrivant</h1>
        <p className="v2-sous-titre">
          Renseignez les informations du nouveau collaborateur : un mail d'information structuré lui est envoyé, ainsi qu'à ses managers.
        </p>
      </header>

      <div className="v2-arrivant-colonnes">
        {/* ── Nouvel arrivant ── */}
        <section className="v2-panneau" aria-labelledby="v2-arrivant-identite">
          <h2 id="v2-arrivant-identite" className="v2-bloc-titre v2-marge-bas">Nouvel arrivant</h2>
          <div className="v2-formulaire">
            <div className="v2-champs-ligne">
              <div className="v2-champ">
                <label className="v2-label" htmlFor="v2-arr-prenom">Prénom <span className="v2-requis" aria-hidden="true">*</span></label>
                <input id="v2-arr-prenom" name="prenom" value={form.prenom} onChange={handleChange} placeholder="Marie" autoComplete="off" />
              </div>
              <div className="v2-champ">
                <label className="v2-label" htmlFor="v2-arr-nom">Nom <span className="v2-requis" aria-hidden="true">*</span></label>
                <input id="v2-arr-nom" name="nom" value={form.nom} onChange={handleChange} placeholder="DUPONT" autoComplete="off" />
              </div>
            </div>

            <div className="v2-champ">
              <label className="v2-label" htmlFor="v2-arr-login">Login Office 365 <span className="v2-requis" aria-hidden="true">*</span></label>
              <input id="v2-arr-login" name="login" value={form.login} onChange={handleChange} placeholder="mdupont@hopscotchgroupe.com" autoComplete="off" />
              <span className="v2-champ-aide">Généré automatiquement (1re lettre du prénom + nom @hopscotchgroupe.com), modifiable.</span>
            </div>

            <div className="v2-champ">
              <label className="v2-label" htmlFor="v2-arr-email">Adresse mail</label>
              <input id="v2-arr-email" name="email" value={form.email} onChange={handleChange} placeholder="mdupont@hopscotch.one" autoComplete="off" />
              <span className="v2-champ-aide">Générée sur le même modèle (@hopscotch.one), à modifier si nécessaire.</span>
            </div>

            <div className="v2-champ">
              <label className="v2-label" htmlFor="v2-arr-tel">Téléphone</label>
              <input id="v2-arr-tel" name="telephone" value={form.telephone} onChange={handleChange} placeholder="Optionnel — ex : +33 6 12 34 56 78" autoComplete="off" />
            </div>

            <div className="v2-champ">
              <label className="v2-label" htmlFor="v2-arr-mdp">Mot de passe temporaire <span className="v2-requis" aria-hidden="true">*</span></label>
              <div className="v2-arrivant-mdp">
                <input
                  id="v2-arr-mdp"
                  name="mot_de_passe"
                  value={form.mot_de_passe}
                  onChange={handleChange}
                  type={mdpVisible ? "text" : "password"}
                  className="v2-champ-code"
                  autoComplete="new-password"
                />
                <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setMdpVisible((v) => !v)}>
                  {mdpVisible ? "Masquer" : "Afficher"}
                </button>
                <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setForm((f) => ({ ...f, mot_de_passe: genererMotDePasse() }))}>
                  <IconeV2 nom="refaire" /> Générer
                </button>
              </div>
              <span className="v2-champ-aide">14 caractères minimum : majuscules, minuscules, chiffres, caractères spéciaux.</span>
            </div>
          </div>
        </section>

        {/* ── Envoi ── */}
        <section className="v2-panneau v2-arrivant-envoi" aria-labelledby="v2-arrivant-envoi">
          <h2 id="v2-arrivant-envoi" className="v2-bloc-titre v2-marge-bas">Envoi du mail</h2>
          <div className="v2-formulaire">
            <div className="v2-champ">
              <label className="v2-label" htmlFor="v2-arr-managers">Managers (destinataires)</label>
              <SelecteurPersonnes
                id="v2-arr-managers"
                selection={managers}
                onChange={setManagers}
                rechercher={(texte) => rechercherUtilisateursEntra(instance, account, texte)}
              />
            </div>

            <label className="v2-case-simple" htmlFor="v2-arr-wedo">
              <input id="v2-arr-wedo" type="checkbox" checked={wedo} onChange={(e) => setWedo(e.target.checked)} />
              <span>Wedo — ajouter alemaitre@hopscotch.one en destinataire</span>
            </label>

            <div className="v2-arrivant-recap">
              <span className="v2-label">Destinataires</span>
              {destinataires.length === 0 ? (
                <span className="v2-champ-aide">—</span>
              ) : (
                <ul className="v2-arrivant-destinataires">
                  {destinataires.map((d) => <li key={d}>{d}</li>)}
                </ul>
              )}
            </div>

            {error && <div className="v2-msg v2-msg-erreur">{error}</div>}
            {success && <div className="v2-msg v2-msg-succes" role="status">{success}</div>}

            <div className="v2-actions">
              <button type="button" className="v2-btn v2-btn-principal" onClick={handleSubmit} disabled={sending}>
                {sending ? "Envoi en cours…" : "Envoyer le mail d'accueil"}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
